import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppShell,
  Footer,
  Sidebar,
  Topbar,
  UserMenuPopover,
  type SidebarItem,
} from "@act-group/act-ui-library";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import ActLogo from "../components/ActLogo/ActLogo";
import { useThemeMode } from "../theme/ThemeModeContext";
import { useUserContext } from "../contexts/UserContext";
import { getApplicationEntry, KYC_APPLICATION_NAME } from "../types/auth";
import { AUTH_LOGOUT_URL } from "../constants/auth";
import { cookiesCleanup } from "../utils/authStorage";

// Menu icons match the SI Console sidebar: 2.5rem white circular chip with a
// black glyph, in both light and dark mode (the SI Console hardcodes these).
const NAV_ICON_SX = {
  fontSize: "2.5rem",
  p: "0.25rem",
  borderRadius: "50%",
  bgcolor: "#ffffff",
  color: "#000000",
} as const;

const NAV_ITEMS: SidebarItem[] = [
  { id: "/", label: "Home", icon: <HomeOutlinedIcon sx={NAV_ICON_SX} />, href: "/" },
];

function MainLayout() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  // Sidebar defaults to collapsed on every screen size; the user can still
  // expand it manually via the hamburger toggle.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleMode } = useThemeMode();
  const { userInfo } = useUserContext();

  useEffect(() => {
    if (isSmallScreen) {
      setSidebarOpen(false);
    }
  }, [isSmallScreen]);

  const activeItemId = useMemo(() => {
    const match = NAV_ITEMS.find(
      (item) =>
        item.href === location.pathname ||
        (item.href !== "/" && location.pathname.startsWith(`${item.href}/`))
    );
    return match?.id;
  }, [location.pathname]);

  const kycApplication = getApplicationEntry(userInfo, KYC_APPLICATION_NAME);
  const userRole = kycApplication?.userTypeName ?? "Guest";
  const userName = userInfo?.name ?? "Guest";

  const handleSignOut = () => {
    cookiesCleanup();
    window.location.href = AUTH_LOGOUT_URL;
  };

  return (
    <AppShell
      footer={
        // The SI Console footer uses its own surface colors (#d8d8d8 light /
        // #1c1c1c dark) rather than the library default (action.hover).
        <Box sx={{ "& > footer": { bgcolor: mode === "dark" ? "#1c1c1c" : "#d8d8d8" } }}>
          <Footer
            logo={<ActLogo height={40} />}
            copyright={`© Copyright ${new Date().getFullYear()} Advanced Compliance Technology Ltd`}
            version="Version 0.1.0"
            // links={[
            //   { id: "terms", label: "Terms of Use", href: "/terms-and-conditions" },
            //   { id: "privacy", label: "Privacy Policy", href: "/privacy-policy" },
            //   { id: "cookies", label: "Cookies Policy", href: "/cookies-policy" },
            //   { id: "contact", label: "Contact Us", href: "/contact" },
            // ]}
          />
        </Box>
      }
      topbar={
        <Topbar
          title="Demo Portal"
          sidebarOpen={sidebarOpen}
          // SI Console pattern: while the sidebar is collapsed the topbar
          // spans the full width (covering the rail's header) and hosts the
          // hamburger; the rail's icons start below it.
          fullWidthWhenCollapsed
          leading={
            !sidebarOpen && (
              <IconButton
                edge="start"
                aria-label="open sidebar"
                onClick={() => setSidebarOpen(true)}
                // mr matches the SI Console's hamburger spacing before the title
                sx={{ mr: 3 }}
              >
                <MenuIcon
                  fontSize="medium"
                  sx={{ color: mode === "dark" ? "#ececec" : "#676767" }}
                />
              </IconButton>
            )
          }
          actions={
            <>
              <IconButton
                aria-label={mode === "dark" ? "switch to light mode" : "switch to dark mode"}
                onClick={toggleMode}
                sx={{
                  // Same circular background treatment as the avatar trigger
                  width: "2.8rem",
                  height: "2.8rem",
                  color: "text.primary",
                  bgcolor: mode === "dark" ? "#121212" : "common.white",
                  "&:hover": { bgcolor: mode === "dark" ? "#404040" : "#dddddd" },
                }}
              >
                {mode === "dark" ? (
                  <Brightness7Icon fontSize="large" />
                ) : (
                  <Brightness4Icon fontSize="large" />
                )}
              </IconButton>
              {/* Brand-styled avatar trigger (matches the SI Console): white
                  circle (dark grey in dark mode), person icon in the text
                  color, green status dot at the top right. */}
              <UserMenuPopover
                clientName="ACT"
                userName={userName}
                userRole={userRole}
                roleLabel={userRole}
                onSignOut={handleSignOut}
                avatarSx={{
                  // bgcolor: mode === "dark" ? "#121212" : "common.white",
                  backgroundColor: mode === "dark" ? "#121212" : "#ffffff",
                  "&:hover": {
                    backgroundColor: mode === "dark" ? "#404040" : "#dddddd",
                  },
                  color: "text.primary",
                  // borderColor: "divider",
                  width: "2.8rem",
                  height: "2.8rem",
                  // Bigger person icon, same icon-to-circle ratio as the SI Console
                  "& .MuiSvgIcon-root": { fontSize: 40 },
                }}
                statusDotSx={{
                  // Sit on the person icon's head (top right), like the SI Console,
                  // rather than on the avatar's outline.
                  // top: 7,
                  // right: 9,
                  // bottom: "auto",
                  // width: 8,
                  // height: 8,
                  // border: "none",
                  // bgcolor: "status.none",
                }}
              />
            </>
          }
        />
      }
      sidebar={
        <Sidebar
          items={NAV_ITEMS}
          open={sidebarOpen}
          activeItemId={activeItemId}
          onNavigate={(item) => item.href && navigate(item.href)}
          header={(expanded) =>
            expanded ? (
              <>
                {/* TODO: redirect to the product selector page once it exists */}
                <ButtonBase
                  aria-label="ACT home"
                  onClick={() => navigate("/")}
                  sx={{ pl: 1, display: "flex", alignItems: "center", borderRadius: 1 }}
                >
                  {/* Same 2rem logo size as the SI Console drawer header */}
                  <ActLogo height={32} />
                </ButtonBase>
                <IconButton
                  size="small"
                  aria-label="close sidebar"
                  onClick={() => setSidebarOpen(false)}
                >
                  {/* Same icon color as the SI Console drawer close button */}
                  <CloseIcon
                    fontSize="medium"
                    sx={{ color: mode === "dark" ? "#ececec" : "#676767" }}
                  />
                </IconButton>
              </>
            ) : (
              // Collapsed: the full-width topbar covers this header, so it
              // only acts as a spacer keeping the rail icons below the topbar
              <Box />
            )
          }
        />
      }
    >
      {/* Cap content width on very wide monitors so pages don't sprawl */}
      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, maxWidth: 1400, mx: "auto", width: "100%" }}>
        <Outlet />
      </Box>
    </AppShell>
  );
}

export default MainLayout;
